<template>
  <div
    class="modal fade"
    :id="modalId"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content text-body">
        <div class="modal-body">
          <div class="row g-1">
            <div class="col-10">
              <button
                type="button"
                class="btn btn-light me-1"
                @click.prevent="fileEvent"
              >
                <i class="bi bi-folder2 me-1"></i>ファイルを選択
              </button>
              <input
                type="file"
                ref="fileInput"
                @change="handleFileChange"
                class="d-none"
                :id="buttonId"
                :accept="accept"
              />
              <button
                class="btn btn-primary"
                type="button"
                v-on:click.prevent="uploadFile"
                :disabled="!selectedFile"
              >
                アップロード
              </button>
            </div>
            <div class="col-2 text-end">
              <button
                type="button"
                class="btn btn-link text-reset"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            <div class="col-12">
              <div v-if="uploadProgress > 0">
                <div class="progress">
                  <div
                    class="progress-bar"
                    role="progressbar"
                    :style="{ width: uploadProgress + '%' }"
                    :aria-valuenow="uploadProgress"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    {{ uploadProgress }}%
                  </div>
                </div>
              </div>
              <div v-if="uploadError" class="alert alert-danger mt-2">
                {{ uploadError }}
              </div>
            </div>
            <div class="col-12">
              <canvas
                ref="canvas"
                width="1600"
                height="900"
                style="display: none"
              ></canvas>
              <!-- 16:9 アスペクト比 -->
              <div class="ratio ratio-16x9 bg-secondary">
                <ImageViewer
                  :file="fileData"
                  :show_caption="false"
                  :show_lightbox="false"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import ImageViewer from '@/components/AppImageViewer.vue'
import { generateRandomFileName } from '@/utils/fileTypeUtils.js'
import { randomString } from '@/utils/stringUtils.js'
import { Modal } from 'bootstrap/dist/js/bootstrap.esm.min.js'
import * as UTIF from 'utif'

export default {
  name: 'PreviewUpload',
  components: {
    ImageViewer,
  },

  props: {
    accept: {
      type: String,
      default: 'video/*, image/*',
    },
    maxSize: {
      type: Number,
      default: 500 * 1024 * 1024, // 動画用: 500MB
    },
    maxImageSize: {
      type: Number,
      default: 20 * 1024 * 1024, // 画像用: 20MB
    },
    uploadUrl: {
      type: String,
      required: true,
    },
  },
  emits: ['uploadComplete', 'hiddenModal'],
  data() {
    return {
      modal: null,
      selectedFile: null,
      fileData: {},
      chunkSize: 5 * 1024 * 1024, // 5MB
      uploadProgress: 0,
      uploadError: null,
      convertedBlob: null,
    }
  },
  computed: {
    modalId: function () {
      return 'form-modal-' + this.randomString()
    },
    buttonId: function () {
      return 'form-modal-' + this.randomString()
    },
  },

  mounted() {
    this.modal = new Modal(document.getElementById(this.modalId))
    document
      .getElementById(this.modalId)
      .addEventListener('hidden.bs.modal', () => {
        this.clearData()
        this.$emit('hiddenModal')
      })
  },
  beforeUnmount() {
    this.modal.hide()
  },

  methods: {
    generateRandomFileName,
    randomString,
    show() {
      this.modal.show()
    },
    fileEvent() {
      document.getElementById(this.buttonId).click()
    },
    handleFileChange(event) {
      const file = event.target.files[0]
      if (!file) return

      const isImage = new RegExp(/image/).test(file.type.toLowerCase())
      const isVideo = new RegExp(/video|mpegurl/).test(file.type.toLowerCase())

      const sizeLimit = isImage ? this.maxImageSize : this.maxSize
      if (file.size > sizeLimit) {
        alert(
          `ファイルサイズが上限（${sizeLimit / (1024 * 1024)}MB）を超えています`
        )
        return
      }

      this.selectedFile = file

      if (isImage) {
        this.createImageThumbnail()
      } else if (isVideo) {
        this.createVideoThumbnail()
      }
    },
    createImageThumbnail() {
      const file = this.selectedFile
      const isTIFF =
        file.type === 'image/tiff' ||
        file.name.endsWith('.tif') ||
        file.name.endsWith('.tiff')

      const reader = new FileReader()

      reader.onload = (event) => {
        const canvas = this.$refs.canvas
        const ctx = canvas.getContext('2d')
        const maxDim = 1920 // 最大幅・高さ

        let drawImagePromise

        if (isTIFF) {
          // TIFF処理（RGBA取得 → 一時Canvas → return Image）
          drawImagePromise = new Promise((resolve, reject) => {
            try {
              const buffer = event.target.result
              const ifds = UTIF.decode(buffer)
              if (!ifds || ifds.length === 0)
                return reject('TIFFの解析に失敗しました')

              UTIF.decodeImage(buffer, ifds[0])
              const rgba = UTIF.toRGBA8(ifds[0])
              const width = ifds[0].width
              const height = ifds[0].height

              const tmpCanvas = document.createElement('canvas')
              tmpCanvas.width = width
              tmpCanvas.height = height
              const tmpCtx = tmpCanvas.getContext('2d')
              const imageData = tmpCtx.createImageData(width, height)
              imageData.data.set(rgba)
              tmpCtx.putImageData(imageData, 0, 0)

              const image = new Image()
              image.onload = () => resolve(image)
              image.src = tmpCanvas.toDataURL()
            } catch (e) {
              reject(e)
            }
          })
        } else {
          // 通常画像処理（Image 読み込み）
          drawImagePromise = new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = () => resolve(img)
            img.onerror = () => reject('画像読み込みに失敗しました')
            img.src = event.target.result
          })
        }

        // WebP変換処理
        drawImagePromise
          .then((img) => {
            const scale = Math.min(1, maxDim / img.width, maxDim / img.height)
            const scaledWidth = Math.round(img.width * scale)
            const scaledHeight = Math.round(img.height * scale)

            canvas.width = scaledWidth
            canvas.height = scaledHeight
            ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight)

            canvas.toBlob(
              (blob) => {
                if (blob) {
                  this.convertedBlob = blob
                  this.fileData = {
                    file_path: URL.createObjectURL(blob),
                    file_type: 'image/webp',
                  }
                }
              },
              'image/webp',
              0.9
            )
          })
          .catch((err) => {
            console.error('画像処理中にエラーが発生しました:', err)
          })
      }

      if (isTIFF) {
        reader.readAsArrayBuffer(file)
      } else {
        reader.readAsDataURL(file)
      }
    },
    createVideoThumbnail() {
      const video = document.createElement('video')
      video.src = URL.createObjectURL(this.selectedFile)
      video.currentTime = 1 // 1秒の位置からサムネイルを作成

      video.addEventListener('loadeddata', () => {
        const canvas = this.$refs.canvas
        const context = canvas.getContext('2d', { willReadFrequently: true })
        context.drawImage(video, 0, 0, canvas.width, canvas.height)
        this.fileData = {
          file_path: canvas.toDataURL('image/png'),
          file_type: 'image/png',
        }
      })
    },
    async uploadFile() {
      if (!this.selectedFile && !this.convertedBlob) return

      // アップロード対象とファイル名・MIMEタイプを決定
      const uploadTarget = this.convertedBlob || this.selectedFile
      const mimeType = this.convertedBlob
        ? 'image/webp'
        : this.selectedFile.type
      const filename = this.generateRandomFileName(mimeType)
      const totalChunks = Math.ceil(uploadTarget.size / this.chunkSize)

      this.uploadProgress = 0
      this.uploadError = null

      for (let i = 0; i < totalChunks; i++) {
        const start = i * this.chunkSize
        const end = Math.min(start + this.chunkSize, uploadTarget.size)
        const chunk = uploadTarget.slice(start, end)

        const formData = new FormData()
        formData.append('file', chunk)
        formData.append('chunk_number', i)
        formData.append('total_chunks', totalChunks)
        formData.append('filename', filename)

        try {
          const response = await api.post(this.uploadUrl, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              )
              this.uploadProgress = Math.round(
                ((i + progress / 100) / totalChunks) * 100
              )
            },
          })
          if (response.data.is_complete) {
            this.$emit('uploadComplete', response.data)
            this.modal.hide()
            break
          }
        } catch (error) {
          console.error(`Error uploading chunk ${i + 1}/${totalChunks}:`, error)
          this.clearData()
          this.uploadError = `Error uploading chunk ${i + 1}: ${error.message}`
          break
        }
      }
      if (!this.uploadError) {
        this.clearData()
      }
    },
    clearData() {
      this.selectedFile = null
      this.convertedBlob = null
      this.fileData = {}
      this.uploadProgress = 0
      this.uploadError = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
  },
}
</script>
